/*
  <adv-gallery>: product photo gallery on advertorial pages.
  One large photo, a row of thumbnails and prev/next arrows (wrapping around). Swipe works on touch screens.
  A change slides the new photo in and fades the old one out (300 ms); instant with "reduce motion" on.
  The new photo is a copy of the large <img> filled from the thumbnail's data-src / data-srcset; the animation
  starts once it has decoded, so there is no blank frame.
*/
if (!customElements.get('adv-gallery')) {
  customElements.define(
    'adv-gallery',
    class AdvGallery extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('.adv-gallery__main');
        this.image = this.querySelector('.adv-gallery__image');
        this.thumbs = Array.from(this.querySelectorAll('.adv-gallery__thumb'));
        this.list = this.querySelector('.adv-gallery__thumbs');
        if (!this.main || !this.image || this.thumbs.length < 2) return;

        this.index = 0;
        this.token = 0;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.show(Number(thumb.dataset.index)));
        });
        this.querySelectorAll('.adv-gallery__arrow').forEach((arrow) => {
          const step = Number(arrow.dataset.step);
          arrow.addEventListener('click', () => this.show(this.index + step, step));
        });

        this.main.addEventListener('touchstart', (event) => {
          this.touchX = event.touches[0].clientX;
          this.touchY = event.touches[0].clientY;
        }, { passive: true });
        this.main.addEventListener('touchend', (event) => {
          if (this.touchX === undefined) return;
          const dx = event.changedTouches[0].clientX - this.touchX;
          const dy = event.changedTouches[0].clientY - this.touchY;
          this.touchX = undefined;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            const step = dx < 0 ? 1 : -1;
            this.show(this.index + step, step);
          }
        }, { passive: true });
      }

      show(index, step) {
        const count = this.thumbs.length;
        const target = (index + count) % count;
        if (target === this.index) return;
        const direction = step || (target > this.index ? 1 : -1);
        this.index = target;

        const thumb = this.thumbs[target];
        this.thumbs.forEach((item) => item.removeAttribute('aria-current'));
        thumb.setAttribute('aria-current', 'true');
        this.scrollThumbIntoView(thumb);

        // A quick second change: drop the photo that is still sliding out and show the current one as is.
        const token = ++this.token;
        this.main.querySelectorAll('.adv-gallery__image').forEach((img) => {
          if (img !== this.image) img.remove();
        });
        const current = this.image;
        current.style.transition = '';
        current.style.opacity = '';
        current.style.transform = '';

        const next = current.cloneNode(false);
        next.removeAttribute('loading');
        next.srcset = thumb.dataset.srcset;
        next.src = thumb.dataset.src;
        next.alt = thumb.dataset.alt || '';
        this.image = next;

        if (this.reduceMotion) {
          current.replaceWith(next);
          return;
        }

        next.style.opacity = '0';
        next.style.transform = `translateX(${direction * 6}%)`;
        current.after(next);

        const ready = next.decode ? next.decode().catch(() => {}) : Promise.resolve();
        ready.then(() => {
          if (token !== this.token) return;
          requestAnimationFrame(() => {
            [current, next].forEach((img) => {
              img.style.transition = 'opacity 300ms ease, transform 300ms ease';
            });
            next.style.opacity = '1';
            next.style.transform = 'translateX(0)';
            current.style.opacity = '0';
            current.style.transform = `translateX(${direction * -6}%)`;
            setTimeout(() => {
              if (current.isConnected && current !== this.image) current.remove();
              if (token === this.token) next.style.transition = '';
            }, 320);
          });
        });
      }

      // Keep the active thumbnail visible without scrolling the page.
      scrollThumbIntoView(thumb) {
        if (!this.list) return;
        const item = thumb.parentElement;
        const left = item.offsetLeft - (this.list.clientWidth - item.offsetWidth) / 2;
        this.list.scrollTo({ left, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }
    }
  );
}
